import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StepEditor, Step, ImageTarget } from '@/components/StepEditor';
import { ImageItem, jsonToImageArray, imageArrayToJson } from '@/lib/image-utils';
import { ContentDirectory } from '@/components/ContentDirectory';
import { ResizablePanelGroup, ResizablePanel } from '@/components/ui/resizable';
import { Loader2, CheckCircle2, Power } from 'lucide-react';
import { toast } from 'sonner';
import { ItemCodesInput } from '@/components/ItemCodesInput';
import { fetchItemCodes } from '@/services/sapProxyApi';
import AppHeader from '@/components/AppHeader';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';

const InstructionEditor = () => {
  const { t } = useTranslation();
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const { user, currentTenantId, currentLineId } = useAuth();
  const { hasPermission, isReady: rolesReady } = useSiteRoles(currentLineId || undefined);
  const modStyling = (supabase as any).schema('mod_styling');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [language, setLanguage] = useState('en');
  const [status, setStatus] = useState<'draft' | 'approved'>('draft');
  const [active, setActive] = useState(true);
  const [steps, setSteps] = useState<Step[]>([]);
  // Item codes state - manages codes assigned to this instruction and available options
  const [itemCodes, setItemCodes] = useState<string[]>([]);
  const [availableItemCodes, setAvailableItemCodes] = useState<string[]>([]);
  const [uploading, setUploading] = useState<Set<string>>(new Set());
  const [activeImageTarget, setActiveImageTarget] = useState<ImageTarget | null>(null);
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const autoSaveTimeout = useRef<NodeJS.Timeout>();
  const currentInstructionId = useRef<string | null>(null);
  // Track last saved state to detect changes efficiently
  const lastSavedState = useRef<{
    title: string;
    description: string;
    language: string;
    itemCodes: string[];
    steps: Step[];
  } | null>(null);

  useEffect(() => {
    if (instructionId) {
      loadInstruction();
    }
    loadAvailableItemCodes();
  }, [instructionId, currentTenantId]);

  usePermissionGuard({
    enabled: rolesReady && !!currentLineId,
    allowed: hasPermission('core.instructions.view'),
    message: t('dashboard.permissionAccessDenied'),
  });

  // Fetch available item codes from SAP API for autocomplete suggestions
  const loadAvailableItemCodes = async () => {
    try {
      const codes = await fetchItemCodes();
      setAvailableItemCodes(codes);
    } catch (error) {
      console.error('Failed to load item codes:', error);
    }
  };

  const loadInstruction = async () => {
    if (!instructionId || !currentTenantId) return;

    try {
      const { data: instruction, error: instError } = await modStyling
        .from('work_instructions')
        .select('*')
        .eq('id', instructionId)
        .eq('tenant_id', currentTenantId)
        .single();

      if (instError) throw instError;

      setTitle(instruction.title);
      setDescription(instruction.description || '');
      setLanguage(instruction.language || 'en');
      setStatus((instruction.status as 'draft' | 'approved') || 'draft');
      setActive(instruction.active ?? true);
      // Load item codes from database, defaulting to empty array if null
      setItemCodes(instruction.item_codes || []);

      const { data: stepsData, error: stepsError } = await modStyling
        .from('instruction_steps')
        .select('*')
        .eq('instruction_id', instructionId)
        .eq('tenant_id', currentTenantId)
        .order('step_number', { ascending: true });

      if (stepsError) throw stepsError;

      // Reconstruct tree structure
      const mainSteps = (stepsData || []).filter(s => !s.parent_step_id);
      const tree: Step[] = mainSteps.map(mainStep => {
        const substeps = (stepsData || [])
          .filter(s => s.parent_step_id === mainStep.id)
          .map(s => ({
            ...s,
            tempId: s.id || `temp-${Date.now()}-${Math.random()}`,
            image_url: jsonToImageArray(s.image_url),
            tools_equipment_image_url: jsonToImageArray(s.tools_equipment_image_url),
            part_number: s.part_number || null,
            tools_equipment: s.tools_equipment || null,
            ppe: s.ppe || null,
            required_roles: s.required_roles || null,
          }));
        
        return {
          ...mainStep,
          tempId: mainStep.id || `temp-${Date.now()}-${Math.random()}`,
          image_url: jsonToImageArray(mainStep.image_url),
          tools_equipment_image_url: jsonToImageArray(mainStep.tools_equipment_image_url),
          part_number: mainStep.part_number || null,
          tools_equipment: mainStep.tools_equipment || null,
          ppe: mainStep.ppe || null,
          required_roles: mainStep.required_roles || null,
          substeps: substeps.length > 0 ? substeps : undefined,
        };
      });

      setSteps(tree);

      // Initialize last saved state
      lastSavedState.current = {
        title: instruction.title,
        description: instruction.description || '',
        language: instruction.language || 'en',
        itemCodes: instruction.item_codes || [],
        steps: JSON.parse(JSON.stringify(tree)), // Deep clone
      };
    } catch (error: any) {
      toast.error(error.message || t('errors.loadInstruction'));
    }
  };

  const handleImageUpload = async (tempId: string, file: File, isToolsImage = false) => {
    if (!file) return;

    const uploadKey = isToolsImage ? `${tempId}-tools` : tempId;
    setUploading(prev => new Set(prev).add(uploadKey));

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `instruction-${Date.now()}-${tempId}${isToolsImage ? '-tools' : ''}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('mod_styling.work_instructions')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('mod_styling.work_instructions')
        .getPublicUrl(fileName);

      // Update the step by adding the new image to the array
      const updateInTree = (tree: Step[]): Step[] => {
        return tree.map(step => {
          if (step.tempId === tempId) {
            const imageField = isToolsImage ? 'tools_equipment_image_url' : 'image_url';
            const currentImages = step[imageField] || [];
            const newImage: ImageItem = { 
              url: publicUrl, 
              order: currentImages.length 
            };
            return {
              ...step,
              [imageField]: [...currentImages, newImage]
            };
          }
          if (step.substeps) {
            return {
              ...step,
              substeps: updateInTree(step.substeps),
            };
          }
          return step;
        });
      };

      setSteps(prev => updateInTree(prev));
      toast.success(t('success.imageUploaded'));
    } catch (error: any) {
      toast.error(error.message || t('errors.uploadImage'));
    } finally {
      setUploading(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadKey);
        return newSet;
      });
    }
  };

  const findStepInTree = (list: Step[], tempId: string): Step | null => {
    for (const step of list) {
      if (step.tempId === tempId) return step;
      if (step.substeps) {
        const found = findStepInTree(step.substeps, tempId);
        if (found) return found;
      }
    }
    return null;
  };

  const extractClipboardImage = useCallback(async (clipboardData?: DataTransfer | null): Promise<File | null> => {
    if (clipboardData?.items) {
      for (const item of Array.from(clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) {
            return new File([blob], `pasted-${Date.now()}.png`, {
              type: blob.type || 'image/png',
            });
          }
        }
      }
    }

    if (navigator.clipboard?.read) {
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const clipboardItem of clipboardItems) {
          const imageType = clipboardItem.types.find(type => type.startsWith('image/'));
          if (imageType) {
            const blob = await clipboardItem.getType(imageType);
            return new File([blob], `pasted-${Date.now()}.png`, { type: imageType });
          }
        }
      } catch (error) {
        console.error('Clipboard read failed', error);
        throw error;
      }
    }

    return null;
  }, []);

  const handlePasteImage = useCallback(
    async (target?: ImageTarget, clipboardData?: DataTransfer | null): Promise<boolean> => {
      const resolvedTarget = target || activeImageTarget;

      if (!resolvedTarget) {
        toast.error(t('editor.selectFieldForPaste') || 'Select an image field before pasting.');
        return false;
      }

      try {
        const file = await extractClipboardImage(clipboardData);

        if (!file) {
          const noAccess = !clipboardData && !navigator.clipboard?.read;
          toast.error(
            noAccess
              ? t('editor.clipboardNotSupported') ||
                  'Clipboard access is not available. Please use the upload button.'
              : t('editor.clipboardNoImage') || 'Clipboard does not contain an image.'
          );
          return false;
        }

        await handleImageUpload(resolvedTarget.tempId, file, resolvedTarget.type === 'tools');
        setActiveImageTarget(resolvedTarget);
        return true;
      } catch (error) {
        toast.error(
          t('editor.clipboardNotSupported') ||
            'Unable to read from clipboard. Please use the upload button instead.'
        );
        return false;
      }
    },
    [activeImageTarget, extractClipboardImage, handleImageUpload, t]
  );

  const handlePasteRequest = useCallback(
    async (target: ImageTarget) => {
      setActiveImageTarget(target);
      const success = await handlePasteImage(target);
      if (!success) {
        toast.error(
          t('editor.clipboardNotSupported') ||
            'Clipboard access is not available. Please use the upload or drag-and-drop options.'
        );
      }
    },
    [handlePasteImage, t]
  );

  // Auto-save function with 2s debounce - persists instruction and item codes
  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    const lastSaved = lastSavedState.current;

    if (!lastSaved) return true; // No save yet, consider as unsaved

    // Compare primitives
    const hasBasicChanges =
      title !== lastSaved.title ||
      description !== lastSaved.description ||
      language !== lastSaved.language ||
      JSON.stringify(itemCodes) !== JSON.stringify(lastSaved.itemCodes);

    if (hasBasicChanges) return true;

    // Compare steps
    const stepsChanged = JSON.stringify(steps) !== JSON.stringify(lastSaved.steps);
    return stepsChanged;
  }, [title, description, language, itemCodes, steps]);

  const autoSave = useCallback(async () => {
    if (!title.trim()) return;
    if (!currentTenantId) return;

    console.log('[AutoSave] Starting auto-save with item codes:', itemCodes);
    setSaving(true);

    try {
      let savedInstructionId = currentInstructionId.current;

      if (savedInstructionId) {
        // Update existing instruction
        console.log('[AutoSave] Updating instruction:', savedInstructionId, 'with item_codes:', itemCodes);
        const { error: updateError } = await modStyling
          .from('work_instructions')
          .update({
            title,
            description,
            language,
            item_codes: itemCodes,
            updated_at: new Date().toISOString(),
          })
          .eq('id', savedInstructionId)
          .eq('tenant_id', currentTenantId);

        if (updateError) {
          console.error('[AutoSave] Update error:', updateError);
          throw updateError;
        }
        console.log('[AutoSave] Successfully updated item_codes');
      } else {
        // Create new instruction as draft
        console.log('[AutoSave] Creating new instruction with item_codes:', itemCodes);
        const { data: newInstruction, error: insertError } = await modStyling
          .from('work_instructions')
          .insert({
            tenant_id: currentTenantId,
            line_id: currentLineId,
            title,
            description,
            language,
            item_codes: itemCodes,
            status: 'draft',
            created_by: user?.id,
            active: true,
          })
          .select()
          .single();

        if (insertError) {
          console.error('[AutoSave] Insert error:', insertError);
          throw insertError;
        }
        console.log('[AutoSave] Successfully created instruction with item_codes');
        savedInstructionId = newInstruction.id;
        currentInstructionId.current = savedInstructionId;

        // Update URL without reloading
        window.history.replaceState(null, '', `/manager/instructions/edit/${savedInstructionId}`);
      }

      // Efficient upsert: get existing steps, then upsert/delete as needed
      if (steps.length > 0) {
        // Get all current steps from database with relevant fields for comparison
        const { data: existingSteps, error: fetchError } = await modStyling
          .from('instruction_steps')
          .select('id, step_number, title, description, parent_step_id')
          .eq('instruction_id', savedInstructionId)
          .eq('tenant_id', currentTenantId);

        if (fetchError) throw fetchError;

        const existingStepsList = (existingSteps as any[]) || [];
        const existingStepIds = new Set(existingStepsList.map(step => step.id));
        const existingStepsMap = new Map(existingStepsList.map(step => [step.id, step]));

        // Separate steps into updates and inserts
        const stepsToUpdate: any[] = [];
        const stepsToInsert: any[] = [];
        const idMapping: Record<string, string> = {}; // tempId -> real id

        // Track which existing IDs are still in use
        const usedStepIds = new Set<string>();

        // Process main steps first
        for (const mainStep of steps) {
          const stepId = mainStep.id && !mainStep.id.startsWith('temp-') ? mainStep.id : undefined;

          const stepData = {
            instruction_id: savedInstructionId,
            step_number: mainStep.step_number,
            title: mainStep.title,
            description: mainStep.description,
            image_url: imageArrayToJson(mainStep.image_url),
            tools_equipment_image_url: imageArrayToJson(mainStep.tools_equipment_image_url || []),
            is_quality_check: mainStep.is_quality_check,
            part_number: mainStep.part_number,
            tools_equipment: mainStep.tools_equipment,
            ppe: mainStep.ppe,
            required_roles: mainStep.required_roles || null,
            language,
            parent_step_id: null, // Main steps have no parent
          };

          if (stepId) {
            // Existing step - check if it actually changed
            usedStepIds.add(stepId);
            idMapping[mainStep.tempId] = stepId;

            const existingStep = existingStepsMap.get(stepId);
            const hasChanged = !existingStep ||
              existingStep.step_number !== mainStep.step_number ||
              existingStep.title !== mainStep.title ||
              existingStep.description !== mainStep.description;

            if (hasChanged) {
              stepsToUpdate.push({ id: stepId, ...stepData });
            }
          } else {
            // New step - insert
            stepsToInsert.push({ ...stepData, _tempId: mainStep.tempId });
          }
        }

        // Update existing steps - simple direct updates now that step_number is not in unique constraint
        if (stepsToUpdate.length > 0) {
          console.log(`[AutoSave] Updating ${stepsToUpdate.length} main steps`);
        }
        for (const step of stepsToUpdate) {
          const { id, ...updateData } = step;
          const { error: updateError } = await modStyling
            .from('instruction_steps')
            .update(updateData)
            .eq('id', id)
            .eq('tenant_id', currentTenantId);

          if (updateError) throw updateError;
        }

        // Insert new steps
        if (stepsToInsert.length > 0) {
          const insertData = stepsToInsert.map(({ _tempId, ...rest }) => ({
            tenant_id: currentTenantId,
            ...rest,
          }));
          const { data: insertedSteps, error: insertError } = await modStyling
            .from('instruction_steps')
            .insert(insertData)
            .select('id');

          if (insertError) throw insertError;

          // Map newly inserted IDs back to steps
          stepsToInsert.forEach((stepData, index) => {
            const newId = insertedSteps?.[index]?.id;
            if (newId) {
              const mainStep = steps.find(s => s.tempId === stepData._tempId);
              if (mainStep) {
                mainStep.id = newId;
                idMapping[mainStep.tempId] = newId;
                usedStepIds.add(newId);
              }
            }
          });
        }

        // Now handle substeps
        const substepsToUpdate: any[] = [];
        const substepsToInsert: any[] = [];

        for (const mainStep of steps) {
          const mainStepId = idMapping[mainStep.tempId];

          if (mainStep.substeps) {
            for (const substep of mainStep.substeps) {
              const substepId = substep.id && !substep.id.startsWith('temp-') ? substep.id : undefined;

              const substepData = {
                instruction_id: savedInstructionId,
                step_number: substep.step_number,
                title: substep.title,
                description: substep.description,
                image_url: imageArrayToJson(substep.image_url),
                tools_equipment_image_url: imageArrayToJson(substep.tools_equipment_image_url || []),
                is_quality_check: substep.is_quality_check,
                part_number: substep.part_number,
                tools_equipment: substep.tools_equipment,
                ppe: substep.ppe,
                required_roles: substep.required_roles || null,
                language,
                parent_step_id: mainStepId,
              };

              if (substepId) {
                // Existing substep - check if it actually changed
                usedStepIds.add(substepId);

                const existingSubstep = existingStepsMap.get(substepId);

                // Only update if step_number, title, or description changed
                // Don't check parent_step_id because it shouldn't change for existing substeps
                const hasChanged = !existingSubstep ||
                  existingSubstep.step_number !== substep.step_number ||
                  existingSubstep.title !== substep.title ||
                  existingSubstep.description !== substep.description;

                if (hasChanged) {
                  substepsToUpdate.push({ id: substepId, ...substepData });
                }
              } else {
                // New substep - insert
                substepsToInsert.push({ ...substepData, _tempId: substep.tempId, _parentTempId: mainStep.tempId });
              }
            }
          }
        }

        // Update existing substeps - simple direct updates now that step_number is not in unique constraint
        if (substepsToUpdate.length > 0) {
          console.log(`[AutoSave] Updating ${substepsToUpdate.length} substeps`);
        }
        for (const substep of substepsToUpdate) {
          const { id, ...updateData } = substep;
          const { error: updateError } = await modStyling
            .from('instruction_steps')
            .update(updateData)
            .eq('id', id)
            .eq('tenant_id', currentTenantId);

          if (updateError) throw updateError;
        }

        // Insert new substeps
        if (substepsToInsert.length > 0) {
          const insertData = substepsToInsert.map(({ _tempId, _parentTempId, ...rest }) => ({
            tenant_id: currentTenantId,
            ...rest,
          }));
          const { data: insertedSubsteps, error: insertError } = await modStyling
            .from('instruction_steps')
            .insert(insertData)
            .select('id');

          if (insertError) throw insertError;

          // Map newly inserted substep IDs back
          substepsToInsert.forEach((substepData, index) => {
            const newId = insertedSubsteps?.[index]?.id;
            if (newId) {
              const mainStep = steps.find(s => s.tempId === substepData._parentTempId);
              const substep = mainStep?.substeps?.find(s => s.tempId === substepData._tempId);
              if (substep) {
                substep.id = newId;
                usedStepIds.add(newId);
              }
            }
          });
        }

        // Delete steps that were removed (exist in DB but not in current state)
        const stepsToDelete = Array.from(existingStepIds).filter(id => !usedStepIds.has(id));
        if (stepsToDelete.length > 0) {
          const { error: deleteError } = await modStyling
            .from('instruction_steps')
            .delete()
            .in('id', stepsToDelete)
            .eq('tenant_id', currentTenantId);

          if (deleteError) throw deleteError;
        }
      } else {
        // No steps - delete all
        const { error: deleteAllError } = await modStyling
          .from('instruction_steps')
          .delete()
          .eq('instruction_id', savedInstructionId)
          .eq('tenant_id', currentTenantId);

        if (deleteAllError) throw deleteAllError;
      }

      // Update last saved state after successful save
      lastSavedState.current = {
        title,
        description,
        language,
        itemCodes,
        steps: JSON.parse(JSON.stringify(steps)), // Deep clone
      };
    } catch (error: any) {
      console.error('[AutoSave] Auto-save error:', error);
      toast.error(t('editor.autoSaveFailed') || `Auto-save failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [title, description, language, itemCodes, steps, user?.id, t, currentTenantId, currentLineId]);

  // Trigger auto-save when data changes
  useEffect(() => {
    if (autoSaveTimeout.current) {
      clearTimeout(autoSaveTimeout.current);
    }

    autoSaveTimeout.current = setTimeout(() => {
      // Check if anything actually changed before calling autoSave
      const lastSaved = lastSavedState.current;

      if (!lastSaved) {
        // First save, always proceed
        autoSave();
        return;
      }

      // Quick check: compare primitives first
      const hasBasicChanges =
        title !== lastSaved.title ||
        description !== lastSaved.description ||
        language !== lastSaved.language ||
        JSON.stringify(itemCodes) !== JSON.stringify(lastSaved.itemCodes);

      // Check steps count change (fast)
      const currentStepsCount = steps.length + steps.reduce((sum, s) => sum + (s.substeps?.length || 0), 0);
      const lastStepsCount = lastSaved.steps.length + lastSaved.steps.reduce((sum, s) => sum + (s.substeps?.length || 0), 0);
      const hasStepCountChange = currentStepsCount !== lastStepsCount;

      if (hasBasicChanges || hasStepCountChange) {
        console.log('[AutoSave] Changes detected, saving...');
        autoSave();
        return;
      }

      // Deep check steps only if counts match (to catch edits within existing steps)
      const stepsChanged = JSON.stringify(steps) !== JSON.stringify(lastSaved.steps);

      if (stepsChanged) {
        console.log('[AutoSave] Step content changed, saving...');
        autoSave();
      } else {
        console.log('[AutoSave] No changes detected, skipping save');
      }
    }, 2000); // 2 second debounce

    return () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
    };
  }, [title, description, language, itemCodes, steps, autoSave]);

  // Initialize current instruction ID
  useEffect(() => {
    if (instructionId) {
      currentInstructionId.current = instructionId;
    }
  }, [instructionId]);

  useEffect(() => {
    if (!activeImageTarget) return;
    const exists = findStepInTree(steps, activeImageTarget.tempId);
    if (!exists) {
      setActiveImageTarget(null);
    }
  }, [activeImageTarget, steps]);

  const handleToggleActive = async () => {
    const instructionToUpdate = currentInstructionId.current || instructionId;

    if (!instructionToUpdate) {
      toast.error(t('errors.updateInstruction'));
      return;
    }

    try {
      const { error } = await modStyling
        .from('work_instructions')
        .update({ active: !active })
        .eq('id', instructionToUpdate)
        .eq('tenant_id', currentTenantId);

      if (error) throw error;

      setActive(!active);
      toast.success(active ? t('instructions.deactivated') : t('instructions.activated'));
    } catch (error: any) {
      toast.error(error.message || t('errors.updateInstruction'));
    }
  };

  const handleManualSave = async () => {
    if (!(hasPermission('core.instructions.create') || hasPermission('core.instructions.edit'))) {
      toast.error(t('dashboard.permissionAccessDenied'));
      return;
    }
    await autoSave();
  };

  const handleApprove = async () => {
    if (!(hasPermission('core.instructions.create') || hasPermission('core.instructions.edit'))) {
      toast.error(t('dashboard.permissionAccessDenied'));
      return;
    }

    if (!title.trim()) {
      toast.error(t('editor.enterTitle'));
      return;
    }

    const flattenSteps = (steps: Step[]): Step[] => {
      const flattened: Step[] = [];
      steps.forEach(step => {
        flattened.push(step);
        if (step.substeps) {
          flattened.push(...step.substeps);
        }
      });
      return flattened;
    };

    const allSteps = flattenSteps(steps);

    if (allSteps.length === 0) {
      toast.error(t('editor.addOneStep'));
      return;
    }

    const invalidSteps = allSteps.filter(s => !s.title.trim() || !s.description.trim());
    if (invalidSteps.length > 0) {
      toast.error(t('editor.allStepsRequired'));
      return;
    }

    setLoading(true);

    try {
      // First ensure everything is saved
      await autoSave();

      // Then approve the instruction
      const instructionToApprove = currentInstructionId.current || instructionId;
      
      if (!instructionToApprove) {
        throw new Error('No instruction to approve');
      }

      const { error: approveError } = await modStyling
        .from('work_instructions')
        .update({ status: 'approved' })
        .eq('id', instructionToApprove)
        .eq('tenant_id', currentTenantId);

      if (approveError) throw approveError;

      toast.success(t('editor.approved'));
      navigate('/manager/instructions');
    } catch (error: any) {
      toast.error(error.message || t('errors.approveInstruction'));
    } finally {
      setLoading(false);
    }
  };

  const scrollToStep = (tempId: string) => {
    const element = stepRefs.current[tempId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight briefly
      element.classList.add('ring-2', 'ring-primary');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-primary');
      }, 2000);
    }
  };

  const handleAddStep = () => {
    const newStep: Step = {
      tempId: `temp-${Date.now()}-${Math.random()}`,
      step_number: steps.length + 1,
      title: '',
      description: '',
      image_url: null,
      tools_equipment_image_url: null,
      is_quality_check: false,
      evidence_required: false,
      part_number: null,
      tools_equipment: null,
      ppe: null,
    };
    setSteps([...steps, newStep]);
  };

  const handleAddSubstepFromDirectory = (parentTempId: string) => {
    const updateSteps = (steps: Step[]): Step[] => {
      return steps.map(step => {
        if (step.tempId === parentTempId) {
          const currentSubsteps = step.substeps || [];
          const newSubstep: Step = {
            tempId: `temp-${Date.now()}-${Math.random()}`,
            step_number: currentSubsteps.length + 1,
            title: '',
            description: '',
            image_url: null,
            tools_equipment_image_url: null,
            is_quality_check: false,
            evidence_required: false,
            parent_step_id: parentTempId,
            part_number: null,
            tools_equipment: null,
            ppe: null,
          };
          return {
            ...step,
            substeps: [...currentSubsteps, newSubstep],
          };
        }
        if (step.substeps) {
          return {
            ...step,
            substeps: updateSteps(step.substeps),
          };
        }
        return step;
      });
    };
    setSteps(updateSteps(steps));
  };

  const handleBackNavigation = () => {
    if (hasUnsavedChanges() && !saving) {
      if (window.confirm(t('editor.unsavedChanges') || 'You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/manager/instructions');
      }
    } else {
      navigate('/manager/instructions');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted flex flex-col h-screen">
      <AppHeader
        className="sticky top-0 z-10"
        onBack={handleBackNavigation}
        backLabel={t('instructions.backToInstructions')}
        action={(
          <>
            <Button
              onClick={handleManualSave}
              disabled={!(hasPermission('core.instructions.create') || hasPermission('core.instructions.edit')) || !hasUnsavedChanges() || saving}
              variant={hasUnsavedChanges() ? "default" : "outline"}
              size="lg"
              className={hasUnsavedChanges() ? "bg-primary text-primary-foreground" : ""}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('editor.saving')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {t('editor.save')}
                </>
              )}
            </Button>
            {instructionId && (
              <Button
                onClick={handleToggleActive}
                variant="outline"
                size="lg"
                disabled={!(hasPermission('core.instructions.create') || hasPermission('core.instructions.edit'))}
              >
                <Power className="mr-2 h-4 w-4" />
                {active ? t('instructions.deactivate') : t('instructions.activate')}
              </Button>
            )}
              <Button
                onClick={handleApprove}
                disabled={!(hasPermission('core.instructions.create') || hasPermission('core.instructions.edit')) || loading || status === 'approved'}
                size="lg"
                variant={status === 'approved' ? 'secondary' : 'default'}
              >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('editor.approving')}
                </>
              ) : status === 'approved' ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {t('editor.approved')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {t('editor.approve')}
                </>
              )}
            </Button>
          </>
        )}
      />

      <div className="flex flex-1 overflow-hidden h-full">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          <ContentDirectory
            steps={steps}
            onStepClick={scrollToStep}
            onAddStep={handleAddStep}
            onAddSubstep={handleAddSubstepFromDirectory}
            onStepsReorder={setSteps}
          />

          <ResizablePanel defaultSize={80} className="h-full">
            <main className="overflow-y-auto h-full">
              <div className="container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {instructionId ? t('editor.edit') : t('editor.create')} {t('editor.workInstruction')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="language">{t('editor.language')} *</Label>
                        <Select value={language} onValueChange={setLanguage}>
                          <SelectTrigger className="h-touch">
                            <SelectValue placeholder={t('editor.selectLanguage')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en">{t('editor.english')}</SelectItem>
                            <SelectItem value="cs">{t('editor.czech')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="title">{t('editor.title')} *</Label>
                        <Input
                          id="title"
                          type="text"
                          placeholder={t('editor.titlePlaceholder')}
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          className="h-touch"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">{t('editor.description')}</Label>
                        <Textarea
                          id="description"
                          placeholder={t('editor.descPlaceholder')}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={3}
                        />
                      </div>

                      <ItemCodesInput
                        value={itemCodes}
                        onChange={(codes) => {
                          console.log('[InstructionEditor] Item codes changed to:', codes);
                          setItemCodes(codes);
                        }}
                        suggestions={availableItemCodes}
                      />
                    </CardContent>
                  </Card>

                  <StepEditor
                    steps={steps}
                    onStepsChange={setSteps}
                    uploading={uploading}
                    onImageUpload={handleImageUpload}
                    onImageTargetFocus={setActiveImageTarget}
                    onPasteImage={handlePasteRequest}
                    stepRefs={stepRefs}
                  />
                </div>
              </div>
            </main>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
};

export default InstructionEditor;
